import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Button, DatePicker, Form, Input, Modal, Popconfirm, Radio, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';

// ── MM:SS helpers ─────────────────────────────────────────────────────────────

function secsToMmss(secs) {
  if (secs == null || isNaN(secs)) return '';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function mmssToSecs(str) {
  if (!str) return null;
  const m = str.match(/^(\d+):([0-5]\d)$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

function fmtDuration(secs) {
  if (secs == null || isNaN(secs)) return '';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  const parts = [];
  if (m > 0) parts.push(`${m} min`);
  if (s > 0 || m === 0) parts.push(`${s} sec`);
  return parts.join(' ');
}

function fmtMonth(yyyymm) {
  if (!yyyymm) return null;
  return dayjs(yyyymm, 'YYYY-MM').format('MMM YYYY');
}

// ── MM:SS Form Input ──────────────────────────────────────────────────────────

function MmssInput({ value, onChange }) {
  const [text, setText] = useState(() => secsToMmss(value));
  const lastReported = useRef(value);

  useLayoutEffect(() => {
    if (value !== lastReported.current) {
      lastReported.current = value;
      setText(secsToMmss(value));
    }
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    setText(raw);
    const secs = mmssToSecs(raw);
    lastReported.current = secs;
    onChange?.(secs != null ? secs : (raw === '' ? undefined : null));
  };

  const parsedSecs = mmssToSecs(text);
  const isError = text.length > 0 && parsedSecs === null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Input
        value={text}
        onChange={handleChange}
        placeholder="M:SS"
        status={isError ? 'error' : undefined}
        style={{ width: 100 }}
      />
      {parsedSecs != null && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          = {fmtDuration(parsedSecs)}
        </Typography.Text>
      )}
      {isError && (
        <Typography.Text type="danger" style={{ fontSize: 12 }}>
          Use M:SS format (e.g. 6:30)
        </Typography.Text>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CampaignGoalsAdminPage() {
  const currentUser = useContext(UserContext);
  const canEdit = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';

  const [goals,    setGoals]    = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [metrics,   setMetrics]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [scope,     setScope]     = useState('campaign');
  const [campaignAvg, setCampaignAvg] = useState(null);
  const [avgLoading,  setAvgLoading]  = useState(false);
  const [form] = Form.useForm();

  const watchedCampaign = Form.useWatch('campaign_id', form);
  const watchedGoalType = Form.useWatch('goal_type',   form);

  const load = () => {
    setLoading(true);
    api.get('/api/admin/campaign-goals')
      .then((r) => setGoals(r.data))
      .catch(() => message.error('Failed to load goals.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/api/admin/campaigns').then((r) => setCampaigns(r.data)).catch(() => {});
    api.get('/api/admin/goal-metrics').then((r) => setMetrics(r.data)).catch(() => {});
  }, []);

  // Fetch campaign average when scope + campaign + goal type are set in the modal
  const effectiveCampaignId = scope === 'overall' ? 0 : watchedCampaign;
  useEffect(() => {
    if (!modalOpen || effectiveCampaignId == null || !watchedGoalType) {
      setCampaignAvg(null);
      return;
    }
    if (scope === 'campaign' && !watchedCampaign) {
      setCampaignAvg(null);
      return;
    }
    setAvgLoading(true);
    api.get('/api/admin/campaign-goals/campaign-avg', {
      params: { campaign_id: effectiveCampaignId, goal_type: watchedGoalType },
    })
      .then((r) => setCampaignAvg(r.data))
      .catch(() => setCampaignAvg(null))
      .finally(() => setAvgLoading(false));
  }, [modalOpen, effectiveCampaignId, watchedGoalType, scope]);

  const openCreate = () => {
    setEditItem(null);
    setScope('campaign');
    form.resetFields();
    form.setFieldsValue({ end_month: null, full_time: '' });
    setCampaignAvg(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    const recordScope = record.CampaignID === 0 ? 'overall' : 'campaign';
    setEditItem(record);
    setScope(recordScope);
    form.setFieldsValue({
      campaign_id:    record.CampaignID === 0 ? undefined : record.CampaignID,
      goal_type:      record.GoalType,
      start_month:    dayjs(record.StartMonth, 'YYYY-MM'),
      end_month:      record.EndMonth ? dayjs(record.EndMonth, 'YYYY-MM') : null,
      silver_value:   record.SilverValue ?? undefined,
      target_value:   record.TargetValue,
      platinum_value: record.PlatinumValue ?? undefined,
      full_time:      record.FullTime == null ? '' : record.FullTime ? '1' : '0',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }

    if (values.target_value == null || isNaN(values.target_value)) {
      message.error('Please enter a valid MM:SS target value.');
      return;
    }

    const rawFT = values.full_time;
    const payload = {
      campaign_id:    scope === 'overall' ? 0 : values.campaign_id,
      goal_type:      values.goal_type,
      start_month:    values.start_month.format('YYYY-MM'),
      end_month:      values.end_month ? values.end_month.format('YYYY-MM') : null,
      silver_value:   values.silver_value ?? null,
      target_value:   values.target_value,
      platinum_value: values.platinum_value ?? null,
      full_time:      rawFT === '1' ? 1 : rawFT === '0' ? 0 : null,
    };

    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/api/admin/campaign-goals/${editItem.ID}`, payload);
        message.success('Goal updated.');
      } else {
        await api.post('/api/admin/campaign-goals', payload);
        message.success('Goal created.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save goal.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/campaign-goals/${id}`);
      message.success('Goal deleted.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to delete goal.');
    }
  };

  const metricMap = Object.fromEntries(metrics.map((m) => [m.key, m]));
  const scopedMetrics = metrics.filter((m) => m.scope === scope);

  const columns = [
    {
      title: 'Campaign',
      dataIndex: 'CampaignName',
      key: 'CampaignName',
      render: (v, record) => record.CampaignID === 0
        ? <Tag color="blue">Overall (Non-Campaign)</Tag>
        : v,
      sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || ''),
    },
    {
      title: 'Metric',
      dataIndex: 'GoalType',
      key: 'GoalType',
      render: (v) => metricMap[v]?.label ?? v,
    },
    {
      title: 'Applies To',
      dataIndex: 'FullTime',
      key: 'FullTime',
      width: 110,
      render: (v) => v == null
        ? <Typography.Text type="secondary">All</Typography.Text>
        : v ? <Tag color="blue">Full Time</Tag> : <Tag>Part Time</Tag>,
    },
    {
      title: 'Start Month',
      dataIndex: 'StartMonth',
      key: 'StartMonth',
      width: 120,
      render: (v) => fmtMonth(v),
    },
    {
      title: 'End Month',
      dataIndex: 'EndMonth',
      key: 'EndMonth',
      width: 120,
      render: (v) => v ? fmtMonth(v) : <Tag>Open-ended</Tag>,
    },
    {
      title: 'Silver',
      dataIndex: 'SilverValue',
      key: 'SilverValue',
      width: 90,
      render: (v, record) => {
        if (v == null) return <Typography.Text type="secondary">—</Typography.Text>;
        const m = metricMap[record.GoalType];
        if (!m) return v;
        if (m.format === 'mmss') return secsToMmss(v);
        if (m.format === 'pct')  return `${v}%`;
        return v;
      },
    },
    {
      title: 'Gold',
      dataIndex: 'TargetValue',
      key: 'TargetValue',
      width: 90,
      render: (v, record) => {
        const m = metricMap[record.GoalType];
        if (!m) return v;
        if (m.format === 'mmss') return secsToMmss(v);
        if (m.format === 'pct')  return `${v}%`;
        return v;
      },
    },
    {
      title: 'Platinum',
      dataIndex: 'PlatinumValue',
      key: 'PlatinumValue',
      width: 90,
      render: (v, record) => {
        if (v == null) return <Typography.Text type="secondary">—</Typography.Text>;
        const m = metricMap[record.GoalType];
        if (!m) return v;
        if (m.format === 'mmss') return secsToMmss(v);
        if (m.format === 'pct')  return `${v}%`;
        return v;
      },
    },
    ...(canEdit ? [{
      title: '',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this goal?"
            onConfirm={() => handleDelete(record.ID)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  const selectedMetric = watchedGoalType ? metricMap[watchedGoalType] : null;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Goals</Typography.Title>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Goal
          </Button>
        )}
      </div>

      <Table
        rowKey="ID"
        dataSource={goals}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 25, showTotal: (t) => `${t} goals` }}
      />

      <Modal maskClosable={false}
        title={editItem ? 'Edit Goal' : 'New Goal'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Save"
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }} key={editItem ? editItem.ID : 'create'}>
          <Form.Item label="Scope">
            <Radio.Group
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                form.setFieldsValue({ campaign_id: undefined, goal_type: undefined });
                setCampaignAvg(null);
              }}
            >
              <Radio.Button value="campaign">Campaign-specific</Radio.Button>
              <Radio.Button value="overall">Overall (Non-Campaign)</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {scope === 'campaign' && (
            <Form.Item
              label="Campaign"
              name="campaign_id"
              rules={[{ required: true, message: 'Select a campaign' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select campaign"
                options={campaigns
                  .filter((c) => c.IsActive)
                  .map((c) => ({ value: c.ID, label: c.Name }))}
              />
            </Form.Item>
          )}

          <Form.Item
            label="Metric"
            name="goal_type"
            rules={[{ required: true, message: 'Select a metric' }]}
          >
            <Select
              placeholder="Select metric"
              options={scopedMetrics.map((m) => ({ value: m.key, label: m.label }))}
            />
          </Form.Item>

          {watchedGoalType === 'UpsellCount' && (
            <Form.Item
              label="Applies To"
              name="full_time"
              initialValue=""
              extra="Use Full Time / Part Time to set separate goals for each group."
            >
              <Select style={{ width: 200 }}>
                <Select.Option value="">All agents</Select.Option>
                <Select.Option value="1">Full Time only</Select.Option>
                <Select.Option value="0">Part Time only</Select.Option>
              </Select>
            </Form.Item>
          )}

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              label="Start Month"
              name="start_month"
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1 }}
            >
              <DatePicker
                picker="month"
                format="MMM YYYY"
                style={{ width: '100%' }}
                placeholder="Start"
              />
            </Form.Item>
            <Form.Item
              label="End Month"
              name="end_month"
              extra="Leave blank for open-ended"
              style={{ flex: 1 }}
            >
              <DatePicker
                picker="month"
                format="MMM YYYY"
                style={{ width: '100%' }}
                placeholder="End (optional)"
              />
            </Form.Item>
          </div>

          {selectedMetric && (
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              {selectedMetric.direction === 'lower'
                ? 'Lower is better — Platinum is the highest standard, Silver is the lowest.'
                : 'Higher is better — Platinum is the highest standard, Silver is the lowest.'}
            </Typography.Text>
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item label="Silver (optional)" name="silver_value" style={{ flex: 1 }}
              extra={selectedMetric?.format === 'mmss' ? 'M:SS' : selectedMetric?.format === 'pct' ? '%' : undefined}>
              {selectedMetric?.format === 'mmss' ? (
                <MmssInput />
              ) : selectedMetric?.format === 'pct' ? (
                <Input type="number" min={0} max={100} step={0.1} placeholder="e.g. 6" addonAfter="%" />
              ) : (
                <Input type="number" placeholder="Value" />
              )}
            </Form.Item>
            <Form.Item label="Gold (required)" name="target_value" style={{ flex: 1 }}
              rules={[
                { required: true, message: 'Required' },
                {
                  validator: (_, val) =>
                    val != null && !isNaN(val)
                      ? Promise.resolve()
                      : Promise.reject(new Error(selectedMetric?.format === 'mmss' ? 'Enter M:SS' : 'Enter a number')),
                },
              ]}>
              {selectedMetric?.format === 'mmss' ? (
                <MmssInput />
              ) : selectedMetric?.format === 'pct' ? (
                <Input type="number" min={0} max={100} step={0.1} placeholder="e.g. 5.5" addonAfter="%" />
              ) : (
                <Input type="number" placeholder="Value" />
              )}
            </Form.Item>
            <Form.Item label="Platinum (optional)" name="platinum_value" style={{ flex: 1 }}
              extra={selectedMetric?.format === 'mmss' ? 'M:SS' : selectedMetric?.format === 'pct' ? '%' : undefined}>
              {selectedMetric?.format === 'mmss' ? (
                <MmssInput />
              ) : selectedMetric?.format === 'pct' ? (
                <Input type="number" min={0} max={100} step={0.1} placeholder="e.g. 5" addonAfter="%" />
              ) : (
                <Input type="number" placeholder="Value" />
              )}
            </Form.Item>
          </div>

          {selectedMetric && (
            <div style={{ marginBottom: 16, padding: '10px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {avgLoading
                  ? 'Loading campaign average...'
                  : campaignAvg?.avg != null
                    ? `Current campaign avg (${fmtMonth(campaignAvg.month)}): ${
                        selectedMetric.format === 'mmss' ? secsToMmss(campaignAvg.avg)
                          : selectedMetric.format === 'pct' ? `${campaignAvg.avg}%`
                          : campaignAvg.avg
                      }`
                    : 'No recent data available for this campaign and metric.'}
              </Typography.Text>
            </div>
          )}
        </Form>
      </Modal>
    </>
  );
}
